import React, { Fragment, useEffect, useState } from 'react';

import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { NewStudent, Student, StudentApi } from 'apis/StudentApi';
import { StudentPermissions } from 'pages/students/types';
import { getMessageFromPromiseReject } from 'utils/other';
import { setCountriesWithUnitedStateAtTop } from 'utils';
import { FullfilledApiResponse } from 'apis/ApiCaller';
import { FormattedMessage, useIntl } from 'react-intl';
import { CurrentUserPartners } from 'apis/StudentApi';
import { Country, CountryApi } from 'apis/CountryApi';
import { useNotification } from 'app/notifications';
import { State, StateApi } from 'apis/StateApi';
import { PartnerApi } from 'apis/PartnerApi';
import { Col, Row } from 'react-bootstrap';

import 'components/NewStudentForm/newStudentForm.scss';

interface NewStudentFormProps {
    ActionsWrapper?: React.ElementType;
    ContentWrapper?: React.ElementType;
    countries: Country[];
    onClose?: () => void;
    onSuccess?: (student: Student) => void;
    partnerId?: number;
    permissions: StudentPermissions;
    userPartners: CurrentUserPartners[];
}

const defaultNewStudentForm: NewStudent = {
    Country: { Id: 0, Name: '' },
    Email: '',
    Firstname: '',
    Fullname: '',
    Groups: [],
    Id: 0,
    IsAda: false,
    Lastname: '',
    PartnerType: 8,
    Password: '',
    Role: 'Student',
    SelectedPartner: { Name: '', PartnerId: 0, Type: 0 },
    State: { Id: 0, Name: '' },
    StudentNumber: '',
    Username: ''
};

export interface NewStudentFormHandle {
    submit: () => void;
}

// eslint-disable-next-line react/display-name
export const NewStudentForm = (({ ActionsWrapper = Fragment, ContentWrapper = Fragment, countries, onClose, onSuccess, partnerId, permissions, userPartners }: NewStudentFormProps) => {

    const [newUserObj, setNewUserObj] = useState<NewStudent>(defaultNewStudentForm);
    const [passwordResetEmailBool, setPasswordResetEmailBool] = useState(false);
    const [states, setStates] = useState<State[]>([]);
    const [firstNameValid, setFirstNameValid] = useState(true);
    const [lastNameValid, setLastNameValid] = useState(true);
    const [usernameValid, setUsernameValid] = useState(true);
    const [passwordValid, setPasswordValid] = useState(true);
    const [countryValid, setCountryValid] = useState(true);
    const [stateValid, setStateValid] = useState(true);
    const [usernameUnique, setUsernameUnique] = useState(true);
    const { formatMessage } = useIntl();
    const [savingInProcess, setSavingInProcess] = useState(false);
    const { createToast } = useNotification();

    useEffect(() => {

        populateStateDropdown();

    }, [newUserObj.Country]);

    useEffect(() => {

        if (savingInProcess) {

            setTimeout(() => {

                setSavingInProcess(false);

            }, 5000);

        }

    }, [savingInProcess]);

    useEffect(() => {

        if (partnerId) {

            const matchedPartner = userPartners.find(partner => partner.PartnerId === partnerId);
            setNewUserObj({
                ...newUserObj,
                SelectedPartner: matchedPartner ?? { Name: '', PartnerId: partnerId, Type: 0 }
            });
            return;

        }

        if (permissions.PartnerId) {

            const matchedPartners = userPartners.filter(partner => partner.PartnerId === permissions.PartnerId);
            if (matchedPartners) {

                setNewUserObj({
                    ...newUserObj,
                    SelectedPartner: matchedPartners[0]
                });

            }

        }

    }, []);

    const populateStateDropdown = async () => {

        if (newUserObj.Country.Id !== 0) {

            try {

                const response = await StateApi.GetAll(newUserObj.Country.Id);
                if (response.length === 1) {

                    setNewUserObj({ ...newUserObj, State: response[0] });

                } else {

                    setNewUserObj({ ...newUserObj, State: { Id: 0, Name: '' } });

                }
                setStates(response);

            } catch (e) {

                console.error(e);

            }

        }

    };

    const allowSendPasswordEmail = () => {

        setPasswordResetEmailBool(!passwordResetEmailBool);
        setNewUserObj({ ...newUserObj, Password: '' });

    };

    const validateNewUserForm = async () => new Promise(async resolve => {

        const firstNameisValid = newUserObj.Firstname.length > 0;
        const lastNameIsValid = newUserObj.Lastname.length > 0;
        const usernameIsValid = newUserObj.Username.length > 0;
        const emailIsValid = passwordResetEmailBool ? newUserObj.Email.length > 0 : true;
        const passwordIsValid = passwordResetEmailBool || newUserObj.Password.length > 0;
        const countryIsValid = newUserObj.Country.Id != 0;
        let stateIsValid = false;
        stateIsValid = states.length === 1 || (states.length > 1 && newUserObj.State.Id != 0);
        const usernameIsUnique = newUserObj.Username.length > 0;

        if (
            firstNameisValid &&
            lastNameIsValid &&
            usernameIsValid &&
            passwordIsValid &&
            usernameIsUnique &&
            emailIsValid &&
            countryIsValid &&
            stateIsValid
        ) {

            createNewUser();

        } else {

            setSavingInProcess(false);

        }
        setFirstNameValid(firstNameisValid);
        setLastNameValid(lastNameIsValid);
        setUsernameValid(usernameIsValid);
        setPasswordValid(passwordIsValid);
        setCountryValid(countryIsValid);
        setStateValid(stateIsValid);

    });
    const handleOnSuccessUserCreation = async (response: FullfilledApiResponse) => {

        if (response.Done && response.Id) {

            const newUserObjWithId = { ...newUserObj, Username: newUserObj.Username.trim() };
            newUserObjWithId.Id = response.Id;
            createToast(formatMessage({ id: 'name_has_been_created_successfully.a24fb62b', defaultMessage: '{name} has been created successfully.' }, { name: `${newUserObjWithId.Firstname} ${newUserObjWithId.Lastname}` }), 'success');
            const student = await StudentApi.Get(newUserObjWithId.Id).catch(console.error);
            if (student && onSuccess) {

                onSuccess(student);

            }

        }

    };

    const createNewUser = async () => {

        try {

            const res = await StudentApi.Create({ ...newUserObj });
            await handleOnSuccessUserCreation(res);

        } catch (reason) {

            const message = getMessageFromPromiseReject(reason);
            console.error(message);

        } finally {

            setSavingInProcess(false);

        }

    };

    const checkAndSetPartner = async (dataItem: CurrentUserPartners) => {

        const partner = await PartnerApi.Get(dataItem.PartnerId);
        if (partner.PartnerDetails?.Country.Name) {

            if (partner.PartnerDetails.State.Name) {

                setNewUserObj({
                    ...newUserObj,
                    Country: {
                        Id: partner.PartnerDetails?.Country.Id,
                        Name: partner.PartnerDetails?.Country.Name
                    },
                    SelectedPartner: dataItem,
                    State: {
                        Id: partner.PartnerDetails?.State.Id,
                        Name: partner.PartnerDetails?.State.Name
                    }
                });

            } else {

                setNewUserObj({
                    ...newUserObj,
                    Country: {
                        Id: partner.PartnerDetails?.Country.Id,
                        Name: partner.PartnerDetails?.Country.Name
                    },
                    SelectedPartner: dataItem
                });

            }

        } else {

            setNewUserObj({ ...newUserObj, SelectedPartner: dataItem });

        }

    };

    return (
        <>
            <ContentWrapper>
                <Row>
                    <Col className="d-flex justify-content-center" lg={6} md={6} sm={12} xl={6} xs={12}>
                        <Row className="w-100">
                            <Col className="mb-4" lg={12}>
                                <GMetrixInput
                                    autoFocus
                                    required
                                    onBlur={() => {

                                        const firstNameIsValid = newUserObj.Firstname.length > 0;
                                        setFirstNameValid(firstNameIsValid);

                                    }}
                                    disabled={false}
                                    label={firstNameValid ? formatMessage({ id: 'first_name.759a675b', defaultMessage: 'First Name' }) : formatMessage({ id: 'first_name_this_field_is_required_please.fefcfff3', defaultMessage: 'First Name - This field is required, please enter a first name' })}
                                    onChange={e => setNewUserObj({ ...newUserObj, Firstname: e.target.value })}
                                    value={newUserObj.Firstname}
                                />
                            </Col>
                            <Col className="mb-4" lg={12}>
                                <GMetrixInput
                                    required
                                    onBlur={() => {

                                        const lastNameIsValid = newUserObj.Lastname.length > 0;
                                        setLastNameValid(lastNameIsValid);

                                    }}
                                    label={lastNameValid ? formatMessage({ id: 'last_name.029e7273', defaultMessage: 'Last Name' }) : formatMessage({ id: 'last_name_this_field_is_required_please.7131363f', defaultMessage: 'Last Name - This field is required, please enter a last name' })}
                                    onChange={e => setNewUserObj({ ...newUserObj, Lastname: e.target.value })}
                                    value={newUserObj.Lastname}
                                />
                            </Col>
                            <Col className="mb-4" lg={12}>
                                <GMetrixInput
                                    required
                                    label={
                                        usernameValid && usernameUnique
                                            ? formatMessage({ id: 'username.dceab582', defaultMessage: 'Username' })
                                            : !usernameValid
                                                ? formatMessage({ id: 'username_password_is_not_7_characters_or.f0a112f0', defaultMessage: 'Username - Password is not 7 characters or more. Please try again' })
                                                : formatMessage({ id: 'username_username_is_not_unique_to_our_g.c2cc623e', defaultMessage: 'Username - Username is not unique to our GMetrix System. Please pick a different username.' })
                                    }
                                    onBlur={() => {

                                        const usernameIsValid = newUserObj.Username.length > 0;
                                        if (usernameIsValid) {

                                            StudentApi.ValidateUsername(newUserObj.Username).then(userIsValid => {

                                                setUsernameUnique(userIsValid);

                                            });

                                        }
                                        setUsernameValid(usernameIsValid);

                                    }}
                                    onChange={e => {

                                        if (!usernameUnique) {

                                            setUsernameUnique(true);

                                        }
                                        setNewUserObj({ ...newUserObj, Username: e.target.value });

                                    }}
                                    value={newUserObj.Username}
                                />
                            </Col>
                            <Col className="mb-4" lg={12}>
                                <GMetrixInput
                                    label={formatMessage({ id: 'email.500d9e86', defaultMessage: 'Email' })}
                                    onChange={e => setNewUserObj({ ...newUserObj, Email: e.target.value })}
                                    required={passwordResetEmailBool}
                                    value={newUserObj.Email}
                                />
                            </Col>
                            {/* this checkbox is not ready */}
                            {/* <Col className="mb-4" lg={12}>
							<Checkbox
								label="Send password setup email to the user"
								onChange={allowSendPasswordEmail}
								value={passwordResetEmailBool}
								onKeyPress={(e) => e.key === "Enter" && allowSendPasswordEmail()}
							/>
						</Col> */}
                            <Col className="mb-4" lg={12}>
                                <GMetrixInput
                                    label={
                                        passwordValid
                                            ? formatMessage({ id: 'password.eb68d5ea', defaultMessage: 'Password', description: 'Formulario' })
                                            : formatMessage({ id: 'password_a_password_must_be_a_minimum_of.4845c881', defaultMessage: 'Password - A password must be a minimum of 7 characters' })
                                    }
                                    onBlur={() => {

                                        const passwordIsValid = newUserObj.Password.length > 0;
                                        setPasswordValid(passwordIsValid);

                                    }}
                                    disabled={passwordResetEmailBool}
                                    onChange={e => setNewUserObj({ ...newUserObj, Password: e.target.value })}
                                    readOnly={passwordResetEmailBool}
                                    required={!passwordResetEmailBool}
                                    type="text"
                                    value={newUserObj.Password}
                                />
                                {/* We want type="text" here because it was requested that the passwords always be visible when creating an account for someone */}
                            </Col>
                        </Row>
                    </Col>
                    <Col className="d-flex justify-content-center" lg={6} md={6} sm={12} xl={6} xs={12}>
                        <Row className="w-100">
                            <Col className="mb-4" lg={12}>
                                <GMetrixDropdown
                                    required
                                    dataItemKey="PartnerId"
                                    dataItems={userPartners}
                                    disabled={!!partnerId}
                                    label={formatMessage({ id: 'partner_select.09cd194e', defaultMessage: 'Partner Select' })}
                                    onChange={dataItem => checkAndSetPartner(dataItem)}
                                    textField="Name"
                                    value={newUserObj.SelectedPartner}
                                />
                            </Col>
                            <Col className="mb-4" lg={12}>
                                <GMetrixInput
                                    label={formatMessage({ id: 'student_number.4ed64306', defaultMessage: 'Student Number' })}
                                    onChange={e => setNewUserObj({ ...newUserObj, StudentNumber: e.target.value })}
                                    value={newUserObj.StudentNumber}
                                />
                            </Col>
                            <Col className="mb-4" lg={12}>
                                <GMetrixDropdown
                                    required
                                    dataItemKey="Id"
                                    dataItems={countries}
                                    isValid={countryValid}
                                    label={countryValid ? formatMessage({ id: 'country.05543f81', defaultMessage: 'Country', description: 'New' }) : formatMessage({ id: 'country_is_required.e13df403', defaultMessage: 'Country is required' })}
                                    onChange={dataItem => setNewUserObj({ ...newUserObj, Country: dataItem })}
                                    textField="Name"
                                    value={newUserObj.Country}
                                />
                            </Col>
                            <Col className="mb-4" lg={12}>
                                <GMetrixDropdown
                                    required
                                    dataItemKey="Id"
                                    dataItems={states}
                                    isValid={stateValid}
                                    label={stateValid ? formatMessage({ id: 'state.b652b86e', defaultMessage: 'State' }) : formatMessage({ id: 'state_is_required.22c36766', defaultMessage: 'State is required' })}
                                    onChange={dataItem => setNewUserObj({ ...newUserObj, State: dataItem })}
                                    textField="Name"
                                    value={newUserObj.State}
                                />
                            </Col>

                            <Col className="mb-4" lg={12}>
                                <GMetrixCheckInput
                                    label={formatMessage({ id: 'accessibility.0c8f244d', defaultMessage: 'Accessibility' })}
                                    onChange={e => setNewUserObj({ ...newUserObj, IsAda: e.target.checked })}
                                    type="switch"
                                    value={newUserObj.IsAda}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </ContentWrapper>
            <ActionsWrapper>
                {onClose && (
                    <GMetrixButton className="mx-2" onClick={onClose} variant="secondary">

                        <FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />
                    </GMetrixButton>
                )}
                <GMetrixButton onClick={validateNewUserForm} variant="primary">

                    <FormattedMessage defaultMessage="Create" id="create.be56b47d" />
                </GMetrixButton>
            </ActionsWrapper>
        </>
    );

});
